import type { ConfigDraftResult, DraftableKind } from '@cyberscore/contracts';
import { Sparkles, X } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { navigate } from '../../lib/router';
import { AnswerInput } from '../assessments/AnswerInput';
import { useCreateItem, useDraftWithAi, useQuestionInfo } from './api';
import { describe, type Cond } from './conditions';
import { previewQuestion, type QuestionDraft } from './editors';
import { effectLabel } from './EffectsEditor';

const EXAMPLES: Record<DraftableKind, string> = {
  question: 'e.g. Ask how often staff get phishing training: every year, only when they join, or never. It measures the phishing awareness control.', // prettier-ignore
  rule: 'e.g. When a business says nobody uses MFA on email, raise a high finding and ask an underwriter to review it.',
};

/**
 * Drafting with AI (task 47): describe the question or rule, check what the agent drafted, then create it
 * as a draft to finish in the editor. Publishing still needs a release approved by a second person.
 */
export function AiDraft({
  kind,
  open,
  onClose,
}: {
  kind: DraftableKind;
  open: (id: string) => string;
  onClose: () => void;
}) {
  // prettier-ignore
  const draft = useDraftWithAi();
  const create = useCreateItem();
  const [request, setRequest] = useState('');
  const [error, setError] = useState<string | null>(null);
  const r = draft.data;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await draft.mutateAsync({ kind, request: request.trim() });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? 'This month’s AI budget is used up.' : err instanceof Error ? err.message : 'Could not draft it'); // prettier-ignore
    }
  };
  const keep = async () => {
    if (!r) return;
    setError(null);
    try {
      const row = await create.mutateAsync({
        kind,
        key: r.key,
        body: r.body,
        note: `Drafted with AI (${r.trace_id})`,
      });
      navigate(open(row.id));
    } catch (err) {
      setError(err instanceof ApiError ? (err.issues.map((i) => `${i.path} ${i.message}`).join('; ') || err.message) : 'Could not create it'); // prettier-ignore
    }
  };

  return (
    <section className="card form-card mb" aria-labelledby="ai-h">
      <div className="card-head">
        <div id="ai-h" className="card-title row gap">
          <Sparkles size={16} /> Draft a {kind} with AI
        </div>
        <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <form className="form-body" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="ai-req">What do you need?</label>
          <textarea id="ai-req" className="input" rows={3} value={request} onChange={(e) => setRequest(e.target.value)} placeholder={EXAMPLES[kind]} />
          <span className="hint">The draft is checked like any other, and nothing is saved until you create it. A second person approves the release.</span>
        </div>
        <div className="row gap">
          <button type="submit" className="btn btn-primary" disabled={request.trim().length < 10 || draft.isPending}>
            {draft.isPending ? 'Drafting…' : r ? 'Draft again' : 'Draft it'}
          </button>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
      </form>
      {r && <Result r={r} creating={create.isPending} onKeep={keep} />}
    </section>
  ); // prettier-ignore
}

function Result({ r, creating, onKeep }: { r: ConfigDraftResult; creating: boolean; onKeep: () => void }) {
  const questions = useQuestionInfo();
  const byKey = useMemo(() => new Map(questions.map((q) => [q.key, q])), [questions]);
  const [sample, setSample] = useState<unknown>(undefined);
  const q = r.kind === 'question' ? previewQuestion(r.body as unknown as QuestionDraft) : null;
  const rule = r.kind === 'rule' ? (r.body as { name?: string; when?: Cond; then?: { type: string; params?: Record<string, unknown> }[] }) : null; // prettier-ignore
  return (
    <div className="form-body ai-result" role="status">
      <div className="row-between wrap gap">
        <div>
          <span className="mono">{r.key}</span> <span className="muted small">· {r.model} · {r.attempts === 1 ? 'first try' : 'after one repair'}</span>
        </div>
        {r.issues.length === 0 ? <span className="pill ok">Passes the checks</span> : <span className="pill warn">{r.issues.length} to fix</span>}
      </div>
      {r.rationale && <p className="small">{r.rationale}</p>}
      {r.assumptions.length > 0 && (
        <ul className="effect-list small muted">
          {r.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
      {r.issues.length > 0 && (
        <ul className="problem-list">
          {r.issues.map((i, n) => (
            <li key={n}>
              <b>{i.path || 'body'}:</b> {i.message}
            </li>
          ))}
        </ul>
      )}
      {q && (
        <div className="preview card pad">
          <h3 className="preview-q">{q.text}</h3>
          {q.help && <p className="why">{q.help}</p>}
          <AnswerInput id="ai-sample" q={q} value={sample} onChange={setSample} onPick={setSample} errors={{}} />
        </div>
      )}
      {rule && (
        <div className="card pad small">
          <b>{rule.name}</b>
          <div className="muted">When {describe(rule.when, byKey)}</div>
          <ul className="effect-list">
            {(rule.then ?? []).map((e, n) => (
              <li key={n}>
                {effectLabel(e.type)} {e.params && Object.keys(e.params).length ? <span className="mono muted">{JSON.stringify(e.params)}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      <details>
        <summary className="small">The draft as JSON</summary>
        <pre className="mono small json-block">{JSON.stringify(r.body, null, 2)}</pre>
      </details>
      <div className="row gap">
        <button type="button" className="btn btn-primary" onClick={onKeep} disabled={creating}>
          Create as draft and edit
        </button>
      </div>
    </div>
  ); // prettier-ignore
}
